/* LPS — ürün sayfasında 3B görüntüleyici. Model galeri kaidesinin üstünde durur, gölgesi kaideye düşer.
   Işık ampulle eşlenir: açık = galeri ışıkları (render look L), kapalı = tek spot + kenar ışığı (look A).
   Yatay sürükleme döndürür, dikey sürükleme telefonda sayfayı kaydırmaya devam eder. */
// esm.sh: RoomEnvironment'ın 'three' importunu aynı three örneğine bağlar (temada importmap yok)
const THREE_URL = 'https://esm.sh/three@0.160.0';
const ENV_URL = 'https://esm.sh/three@0.160.0/examples/jsm/environments/RoomEnvironment.js';

const LOOKS = {
  on:  { top: '#3a3a3d', bot: '#18181a', floor: 0x262628, plinth: 0x2e2e31, metal: 0xb9bdc3, metalness: 0.76, rough: 0.34, env: 0.85,
         key: 300, angle: 0.95, pen: 1.0, kpos: [1.0, 3.0, 2.4], fill: 0.6, rim: 1.6 },
  off: { top: '#0b0b0c', bot: '#000000', floor: 0x050505, plinth: 0x101012, metal: 0x949aa2, metalness: 0.93, rough: 0.3, env: 0.45,
         key: 520, angle: 0.32, pen: 0.55, kpos: [0.25, 3.2, 1.4], fill: 0.08, rim: 3.2 },
};

async function start(box) {
  const THREE = await import(THREE_URL);
  const { RoomEnvironment } = await import(ENV_URL).catch(() => ({}));
  const data = await (await fetch(box.dataset.model)).json();

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 0.95;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  box.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  if (RoomEnvironment) scene.environment = new THREE.PMREMGenerator(renderer).fromScene(new RoomEnvironment(), 0.04).texture;

  // model: yükseklik 1, taban y=0
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(data.v, 3));
  geo.computeVertexNormals(); geo.computeBoundingBox();
  const bb = geo.boundingBox, foot = Math.max(bb.max.x - bb.min.x, bb.max.z - bb.min.z);
  const mat = new THREE.MeshStandardMaterial({ flatShading: true, side: THREE.DoubleSide });
  const mesh = new THREE.Mesh(geo, mat); mesh.castShadow = true; mesh.receiveShadow = true;
  const turn = new THREE.Group(); turn.add(mesh); scene.add(turn);

  // kaide: model üstüne oturur, galeri zemininde durur
  const ph = 0.16, pw = foot * 1.18;
  const plinthMat = new THREE.MeshStandardMaterial({ roughness: 0.85, metalness: 0 });
  const plinth = new THREE.Mesh(new THREE.BoxGeometry(pw, ph, pw), plinthMat);
  plinth.position.y = -ph / 2; plinth.receiveShadow = true; plinth.castShadow = true; turn.add(plinth);
  const floorMat = new THREE.MeshStandardMaterial({ roughness: 0.92, metalness: 0 });
  const floor = new THREE.Mesh(new THREE.CircleGeometry(8, 64), floorMat);
  floor.rotation.x = -Math.PI / 2; floor.position.y = -ph; floor.receiveShadow = true; scene.add(floor);

  const s = Math.max(1, foot);
  const spot = new THREE.SpotLight(0xffffff, 1, 0, 0.5, 1, 0);
  spot.target.position.set(0, 0.45, 0); scene.add(spot.target);
  spot.castShadow = true; spot.shadow.mapSize.set(1024, 1024); spot.shadow.bias = -0.0004; spot.shadow.radius = 6;
  scene.add(spot);
  const rim = new THREE.DirectionalLight(0xdfe8ff, 1); rim.position.set(-2.4, 1.8, -1.6); scene.add(rim);
  const fill = new THREE.HemisphereLight(0xe8eef6, 0x000000, 0.3); scene.add(fill);

  const bgCanvas = document.createElement('canvas'); bgCanvas.width = 4; bgCanvas.height = 256;
  const bgTex = new THREE.CanvasTexture(bgCanvas); bgTex.colorSpace = THREE.SRGBColorSpace; scene.background = bgTex;

  const cam = new THREE.PerspectiveCamera(30, 1, 0.01, 100);
  // başlangıç açısı modele göre (data-yaw), kadraj kaideyle birlikte
  const view = { yaw: parseFloat(box.dataset.yaw || '-0.6'), pitch: 0.24, dist: 0 }, fitDist = (2.1 / (2 * Math.tan(THREE.MathUtils.degToRad(15)))) * Math.max(1, foot * 0.9);
  view.dist = fitDist;
  scene.fog = new THREE.Fog(0x000000, fitDist * 1.1, fitDist * 2.6);

  function applyLook() {
    const L = LOOKS[document.documentElement.dataset.light === 'on' ? 'on' : 'off'];
    const g = bgCanvas.getContext('2d'), gr = g.createLinearGradient(0, 0, 0, 256);
    gr.addColorStop(0, L.top); gr.addColorStop(1, L.bot); g.fillStyle = gr; g.fillRect(0, 0, 4, 256); bgTex.needsUpdate = true;
    scene.fog.color.set(L.bot);
    mat.color.set(L.metal); mat.metalness = L.metalness; mat.roughness = L.rough; mat.envMapIntensity = L.env;
    plinthMat.color.set(L.plinth); floorMat.color.set(L.floor);
    spot.intensity = L.key * 0.0135; spot.angle = L.angle; spot.penumbra = L.pen;
    spot.position.set(L.kpos[0] * s, L.kpos[1] * s, L.kpos[2] * s);
    fill.intensity = L.fill; rim.intensity = L.rim;
    draw();
  }

  function size() {
    const w = box.clientWidth, h = box.clientHeight;
    renderer.setSize(w, h, false); cam.aspect = w / h; cam.updateProjectionMatrix(); draw();
  }

  let queued = false;
  function draw() { if (!queued) { queued = true; requestAnimationFrame(frame); } }
  function frame() {
    queued = false;
    turn.rotation.y = view.yaw;
    const d = view.dist / Math.min(1, cam.aspect);   // dar ekranda model kadraja sığsın
    cam.position.set(0, 0.4 + Math.sin(view.pitch) * d, Math.cos(view.pitch) * d);
    cam.lookAt(0, 0.36, 0);
    renderer.render(scene, cam);
    if (spinning && visible) { view.yaw += 0.0035; draw(); }
  }

  // etkileşim: ilk dokunuşa kadar yavaşça döner
  let spinning = !matchMedia('(prefers-reduced-motion: reduce)').matches, visible = true, drag = null;
  const el = renderer.domElement;
  el.addEventListener('pointerdown', e => { spinning = false; drag = { x: e.clientX, y: e.clientY, yaw: view.yaw, pitch: view.pitch, id: e.pointerId }; box.classList.add('used'); });
  el.addEventListener('pointermove', e => {
    if (!drag || drag.id !== e.pointerId) return;
    view.yaw = drag.yaw + (e.clientX - drag.x) * 0.01;
    if (e.pointerType === 'mouse') view.pitch = Math.min(0.75, Math.max(0.02, drag.pitch + (e.clientY - drag.y) * 0.006));
    draw();
  });
  ['pointerup', 'pointercancel', 'pointerleave'].forEach(t => el.addEventListener(t, () => { drag = null; }));
  el.addEventListener('wheel', e => { e.preventDefault(); view.dist = Math.min(fitDist * 1.5, Math.max(fitDist * 0.55, view.dist * (1 + e.deltaY * 0.001))); draw(); }, { passive: false });

  new IntersectionObserver(es => { visible = es[0].isIntersecting; if (visible) draw(); }).observe(box);
  new ResizeObserver(size).observe(box);
  new MutationObserver(applyLook).observe(document.documentElement, { attributes: true, attributeFilter: ['data-light'] });
  applyLook(); size();
  box.classList.add('ready');
}

function init() {
  const box = document.querySelector('.lps-3d[data-model]');
  if (!box) return;
  const go = () => start(box).catch(err => { console.warn('LPS 3D', err); box.classList.add('failed'); });
  'requestIdleCallback' in window ? requestIdleCallback(go, { timeout: 1500 }) : setTimeout(go, 300);
}
init();
